/**
 * The camera you have — mount and sensor format, held together (REQ-WEB-025/026).
 *
 * Not a filter. Every other control narrows what you would *like*; this one
 * decides whether a lens can physically go on your camera. An RF lens is not a
 * worse match for a Sony body, it is unusable.
 *
 * Two rules keep it honest:
 *
 * 1. **The URL wins over the memory.** The choice is remembered between visits,
 *    but a mount named in the URL always beats the remembered one. Otherwise
 *    the same shared link would show different results to different people —
 *    a correctness failure wearing a convenience costume (REQ-WEB-002).
 * 2. **Compatibility comes from data.** Which lenses cover which sensors is a
 *    fact about the systems, carried as ranks on /mounts. Nothing here decides
 *    that full frame is bigger than APS-C; the server says so.
 */
import type { MountList } from "./api";

const STORE_KEY = "lens-specs.camera";

export interface Camera {
  /** null means "all mounts" — a real option, not a fallback. */
  mount: string | null;
  /** null means "any format on that mount". */
  format: string | null;
  /** Include lenses whose image circle is smaller than the sensor. */
  includeSmaller: boolean;
}

export const ALL: Camera = { mount: null, format: null, includeSmaller: false };

export function fromSearch(search: string): Camera | null {
  const p = new URLSearchParams(search);
  const mount = p.get("mount");
  if (mount === null) return null; // the URL is silent; the caller may fall back
  return {
    mount: mount === "" || mount === "all" ? null : mount,
    format: p.get("format") || null,
    includeSmaller: p.get("smaller") === "1",
  };
}

export function toParams(c: Camera): Record<string, string> {
  const out: Record<string, string> = { mount: c.mount ?? "all" };
  if (c.format) out["format"] = c.format;
  if (c.includeSmaller) out["smaller"] = "1";
  return out;
}

/**
 * The filters this camera implies.
 *
 * `covers` rather than `eq` is the whole point: a full-frame lens belongs in an
 * APS-C shooter's results. Choosing `includeSmaller` drops the coverage
 * constraint entirely rather than inverting it — "show me everything that
 * mounts", which is what the opt-in means.
 */
export function toFilters(c: Camera): Record<string, string> {
  const out: Record<string, string> = {};
  if (c.mount) out["filter[mount][eq]"] = c.mount;
  if (c.format && !c.includeSmaller) out["filter[coverage][covers]"] = c.format;
  return out;
}

/**
 * The camera's sensor format, if it names one (REQ-WEB-035, D42).
 *
 * With a mount and a format chosen, a lens's category is the one its focal
 * length gives on that format, so the API is told which. Not tied to
 * `includeSmaller`: that opt-in widens which lenses are listed, it does not
 * change the camera they are judged on.
 */
export function formatOf(c: Camera): string | null {
  return c.mount && c.format ? c.format : null;
}

/** What a list request carries for this camera: its filters, and its format. */
export function toRequest(c: Camera): Record<string, string> {
  const f = formatOf(c);
  return { ...toFilters(c), ...(f ? { format: f } : {}) };
}

/** The query a lens link carries, so a lens page judges its category on the same camera. */
export function lensSearch(c: Camera): string {
  const f = formatOf(c);
  return f && c.mount ? `?${new URLSearchParams({ mount: c.mount, format: f }).toString()}` : "";
}

/** Remembered between visits; only ever a *default* (see rule 1 above). */
export function remembered(): Camera | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Partial<Camera>;
    return {
      mount: typeof c.mount === "string" ? c.mount : null,
      format: typeof c.format === "string" ? c.format : null,
      includeSmaller: c.includeSmaller === true,
    };
  } catch {
    // Private browsing, cleared storage, a browser that refuses: not an error.
    return null;
  }
}

export function remember(c: Camera): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(c));
  } catch {
    /* nothing to do; the URL still carries the choice */
  }
}

/** How to describe the current camera in one short phrase. */
export function describe(c: Camera, data: MountList | null): string {
  if (!c.mount) return "All mounts";
  const m = data?.mounts.find((x) => x.slug === c.mount);
  const mountName = m?.name ?? c.mount;
  if (!c.format) return mountName;
  const f = data?.coverages.find((x) => x.slug === c.format);
  return `${mountName} · ${f?.name ?? c.format}`;
}

/**
 * A sensor size named as the thing being chosen: "APS-C sensor", "Full-frame
 * sensor" (#213, owner 2026-10-03). "Leica M · APS-C" and "Leica M · Full frame"
 * read as duplicates, because they list the same lenses; a sensor is a different
 * thing from a lens, and the label now says so. The size's own name comes from
 * the API ("Full frame", "Medium format"); only the compound "<word> frame" or
 * "<word> format" gains its hyphen, as an adjective before "sensor" does.
 */
export function sensorName(name: string): string {
  return `${name.replace(/^(\S+) (frame|format)$/i, "$1-$2")} sensor`;
}

/** Every body a visitor could pick, built entirely from the API's own data. */
export function options(data: MountList): { camera: Camera; label: string; lenses: number }[] {
  const out: { camera: Camera; label: string; lenses: number }[] = [];
  const total = data.mounts.reduce((n, m) => n + m.lenses, 0);
  out.push({ camera: ALL, label: "All mounts", lenses: total });

  const rank = new Map(data.coverages.map((c) => [c.slug, c.rank]));
  for (const m of data.mounts) {
    // Only the formats this mount's bodies are actually made in (REQ-DM-021).
    // This was a cross-product over every body format, which offered
    // "Fujifilm X · Full frame" — a camera that has never existed. Note the
    // fix is NOT to infer the set from `m.by_coverage`: four lenses here
    // genuinely are full-frame in an X-mount barrel, so that produces the
    // identical phantom. Lens coverage and body format are different facts and
    // neither derives from the other, so the server declares this one.
    for (const cov of m.body_formats) {
      // How many lenses this body could actually use — every lens on the mount
      // whose image circle is at least as large as the sensor.
      const usable = m.by_coverage
        .filter((x) => (rank.get(x.coverage) ?? 0) >= cov.rank)
        .reduce((n, x) => n + x.lenses, 0);
      out.push({
        camera: { mount: m.slug, format: cov.slug, includeSmaller: false },
        label: `${m.name} · ${sensorName(cov.name)}`,
        lenses: usable,
      });
    }
  }
  return out;
}

/**
 * How many lenses on this mount cover *less* than the chosen sensor — and
 * whether they can be mounted at all.
 *
 * The two are different questions and the rank only answers the first. A Sony
 * APS-C lens on a full-frame body mounts and crops; a Canon EF-S lens does not
 * mount, because the rear baffle is physically keyed out. `accepts_smaller`
 * carries that per mount (REQ-DM-018), so the interface can offer the crop and
 * decline to offer the impossibility.
 */
export function smallerCoverage(
  c: Camera,
  data: MountList | null,
): { count: number; mountable: boolean } {
  if (!data || !c.mount || !c.format) return { count: 0, mountable: true };
  const rank = new Map(data.coverages.map((x) => [x.slug, x.rank]));
  const mine = rank.get(c.format) ?? 0;
  const m = data.mounts.find((x) => x.slug === c.mount);
  const count = (m?.by_coverage ?? [])
    .filter((x) => (rank.get(x.coverage) ?? 0) < mine)
    .reduce((n, x) => n + x.lenses, 0);
  return { count, mountable: m?.accepts_smaller_coverage !== false };
}

/** The format a body of this camera would crop to, for naming the compromise. */
export function smallerFormatName(c: Camera, data: MountList | null): string | null {
  if (!data || !c.mount || !c.format) return null;
  const rank = new Map(data.coverages.map((x) => [x.slug, x.rank]));
  const mine = rank.get(c.format) ?? 0;
  const m = data.mounts.find((x) => x.slug === c.mount);
  const smaller = (m?.by_coverage ?? [])
    .map((x) => data.coverages.find((cv) => cv.slug === x.coverage))
    .filter((cv): cv is NonNullable<typeof cv> => !!cv && cv.rank < mine)
    .sort((a, b) => b.rank - a.rank)[0];
  return smaller?.name ?? null;
}
